import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Filter,
  RefreshCw,
  Plus,
  Search,
  SlidersHorizontal,
  GripVertical,
} from "lucide-react";

/**
 * Shared page header used on every list/index page.
 *
 * Row 1 (top):
 *   LEFT  : optional icon tile + Title + subtitle
 *   RIGHT : Filter → Refresh → Primary action (each piece is optional but the
 *           order is fixed so nothing visually jumps between pages)
 *
 * Row 2 (bottom — rendered only if search OR columnControls are passed):
 *   LEFT  : compact Search box
 *   RIGHT : Customize Columns + Rearrange Columns
 *
 * Props:
 *   icon            : a lucide icon component (e.g. Car, Shield)
 *   title           : string
 *   subtitle        : string
 *   onFilter        : () => void (renders Filter button if provided)
 *   filterTestId    : optional override for the Filter button data-testid
 *   onRefresh       : () => void (renders Refresh button if provided)
 *   refreshTestId   : optional override for the Refresh button data-testid
 *   primaryAction   : { label, onClick, icon?, testId? } | null
 *   search          : { value, onChange, onSubmit?, placeholder?, testId? } | null
 *   columnControls  : { onCustomize, onRearrange,
 *                       customizeTestId?, rearrangeTestId? } | null
 *   testId          : optional outer wrapper data-testid
 */
export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  onFilter,
  filterTestId = "filter-btn",
  onRefresh,
  refreshTestId = "refresh-btn",
  primaryAction = null,
  search = null,
  leftSlot = null,
  columnControls = null,
  testId,
}) {
  const PrimaryIcon = primaryAction?.icon || Plus;
  const showRow2 = Boolean(search) || Boolean(columnControls) || Boolean(leftSlot);

  return (
    <div className="space-y-4" data-testid={testId || "page-header"}>
      {/* Row 1 — title + top-right action cluster */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          {Icon && (
            <div className="w-12 h-12 bg-black text-white rounded-sm flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6" strokeWidth={1.5} />
            </div>
          )}
          <div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {onFilter && (
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-sm"
              onClick={onFilter}
              data-testid={filterTestId}
              aria-label="Filter"
            >
              <Filter className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
          {onRefresh && (
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-sm"
              onClick={onRefresh}
              data-testid={refreshTestId}
              aria-label="Refresh"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
          {primaryAction && (
            <Button
              className="rounded-sm bg-black text-white hover:bg-gray-800"
              onClick={primaryAction.onClick}
              data-testid={primaryAction.testId || "primary-action-btn"}
            >
              <PrimaryIcon className="w-4 h-4 mr-1" strokeWidth={1.5} />
              {primaryAction.label}
            </Button>
          )}
        </div>
      </div>

      {/* Row 2 — compact search (left) + column controls (right) */}
      {showRow2 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            {leftSlot}
            {search && (
              <div className="relative w-full sm:w-80">
                <Search
                  className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400"
                  strokeWidth={1.5}
                />
                <Input
                  value={search.value}
                  onChange={(e) =>
                    search.onChange ? search.onChange(e.target.value) : null
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && search.onSubmit) search.onSubmit();
                  }}
                  placeholder={search.placeholder || "Search"}
                  className="rounded-sm h-8 text-xs pl-8"
                  data-testid={search.testId || "page-search"}
                />
              </div>
            )}
          </div>

          {columnControls && (
            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm h-8 text-xs gap-1.5"
                onClick={columnControls.onCustomize}
                data-testid={columnControls.customizeTestId || "customize-columns-btn"}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.5} />
                Customize Columns
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm h-8 text-xs gap-1.5"
                onClick={columnControls.onRearrange}
                data-testid={columnControls.rearrangeTestId || "rearrange-columns-btn"}
              >
                <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5} />
                Rearrange Columns
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PageHeader;
